import styled, { css } from "styled-components";
import {
  brand,
  border,
  radius,
  spacing,
  status,
  surface,
  text,
  typography,
} from "../../theme/tokens";
import { INBOX_AI_RAIL_WIDTH } from "../Inbox/InboxStyled";

const tone = (name) => status[name || "neutral"] || status.neutral;

// ---------------------------------------------------------------------------
// Page shell: pile | decision | decide + tools
// ---------------------------------------------------------------------------

export const PageWrapper = styled.div`
  font-family: ${typography.fontFamily};
  display: flex;
  width: 100%;
  height: 100vh;
  max-height: 100vh;
  background: ${surface.page};
  padding: 10px 0 0 0;
  gap: 8px;
  box-sizing: border-box;
  overflow: hidden;
`;

export const QueueColumn = styled.aside`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 340px;
  height: 100%;
  background: ${surface.surface};
  border-top-left-radius: ${radius.lg};
  border-top-right-radius: ${radius.lg};
  overflow: hidden;
`;

export const QueueHeader = styled.div`
  padding: ${spacing[4]} ${spacing[4]} ${spacing[3]};
  border-bottom: 1px solid ${border.light};
`;

export const Title = styled.h1`
  margin: 0 0 ${spacing[3]};
  font-size: ${typography.size.lg};
  font-weight: ${typography.weight.bold};
  color: ${text.primary};
`;

export const ScopeRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
  flex-wrap: wrap;
`;

export const ScopeTab = styled.button`
  border: 1px solid ${(p) => (p.$active ? brand.primary : border.default)};
  background: ${(p) => (p.$active ? brand.primaryLight : surface.surface)};
  color: ${(p) => (p.$active ? brand.primary : text.secondary)};
  font-family: inherit;
  font-size: ${typography.size.sm};
  font-weight: ${(p) =>
    p.$active ? typography.weight.semibold : typography.weight.medium};
  padding: ${spacing[1]} ${spacing[2.5]};
  border-radius: ${radius.pill};
  cursor: pointer;

  &:hover {
    border-color: ${brand.primary};
  }
`;

export const LinkButton = styled.button`
  margin-left: auto;
  border: none;
  background: none;
  font-family: inherit;
  font-size: ${typography.size.sm};
  color: ${brand.primary};
  cursor: pointer;
  padding: 0;

  &:disabled {
    color: ${text.muted};
    cursor: default;
  }
`;

export const QueueScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${spacing[2]};
`;

export const QueueRow = styled.button`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  text-align: left;
  background: ${(p) => (p.$active ? brand.primaryLight : surface.surface)};
  border: 1px solid ${(p) => (p.$active ? brand.primary : border.light)};
  border-radius: ${radius.md};
  padding: ${spacing[2.5]} ${spacing[3]};
  margin-bottom: ${spacing[1.5]};
  cursor: pointer;
  font-family: inherit;

  &:hover {
    border-color: ${brand.primary};
  }
`;

export const RowTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[2]};
`;

export const RowName = styled.span`
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.semibold};
  color: ${text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RowLine = styled.span`
  font-size: ${typography.size.sm};
  color: ${text.muted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RowNote = styled.span`
  font-size: ${typography.size.sm};
  color: ${text.secondary};
  font-style: italic;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Chip = styled.span`
  display: inline-block;
  flex-shrink: 0;
  font-size: ${typography.size.xs || typography.size.sm};
  font-weight: ${typography.weight.semibold};
  padding: ${spacing[0.5]} ${spacing[2]};
  border-radius: ${radius.pill};
  white-space: nowrap;
  ${(p) => {
    const palette = tone(p.$tone);
    return css`
      color: ${palette.fg};
      background: ${palette.bg};
      border: 1px solid ${palette.border};
    `;
  }}
`;

export const Muted = styled.p`
  margin: ${spacing[3]};
  color: ${text.muted};
  font-size: ${typography.size.base};
`;

export const Banner = styled.div`
  margin: ${spacing[2]};
  border-radius: ${radius.md};
  padding: ${spacing[2.5]} ${spacing[3]};
  font-size: ${typography.size.sm};
  ${(p) => {
    const palette = tone(p.$tone || "failed");
    return css`
      color: ${palette.fg};
      background: ${palette.bg};
      border: 1px solid ${palette.border};
    `;
  }}
`;

// ---------------------------------------------------------------------------
// Middle: what this is, then the decision itself
// ---------------------------------------------------------------------------

export const CenterColumn = styled.section`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  height: 100%;
  gap: 8px;
`;

export const FactsStrip = styled.div`
  background: ${surface.surface};
  border-radius: ${radius.lg};
  padding: ${spacing[3]} ${spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

export const FactsTop = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${spacing[3]};
  flex-wrap: wrap;
`;

export const ClientName = styled.h2`
  margin: 0;
  font-size: ${typography.size.lg};
  font-weight: ${typography.weight.bold};
  color: ${text.primary};
`;

export const FactRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[1]} ${spacing[5]};
  font-size: ${typography.size.base};
  color: ${text.secondary};
`;

export const Fact = styled.span`
  white-space: nowrap;

  strong {
    color: ${text.primary};
    font-weight: ${typography.weight.semibold};
  }
`;

export const SenderNote = styled.div`
  font-size: ${typography.size.base};
  color: ${text.primary};
  background: ${surface.page};
  border-left: 3px solid ${brand.primary};
  padding: ${spacing[1.5]} ${spacing[3]};
  white-space: pre-wrap;
`;

export const FileSwitcher = styled.div`
  display: flex;
  gap: ${spacing[1.5]};
  flex-wrap: wrap;
`;

export const FileChip = styled.button`
  font-family: inherit;
  font-size: ${typography.size.sm};
  border-radius: ${radius.pill};
  padding: ${spacing[0.5]} ${spacing[2.5]};
  cursor: pointer;
  border: 1px solid ${(p) => (p.$active ? brand.primary : border.default)};
  background: ${(p) => (p.$active ? brand.primaryLight : surface.surface)};
  color: ${(p) => (p.$active ? brand.primary : text.secondary)};
  max-width: 340px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CopyList = styled.ul`
  margin: 0;
  padding: ${spacing[1]} 0;
  list-style: none;
  border: 1px solid ${border.default};
  border-radius: ${radius.md};
  background: ${surface.surface};
  max-height: 200px;
  overflow-y: auto;
`;

export const CopyRow = styled.li`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[1]} ${spacing[2]};
  font-size: ${typography.size.sm};
  background: ${(p) => (p.$active ? brand.primaryLight : "transparent")};

  & > button:first-child {
    margin-left: 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
  }

  & > span {
    color: ${text.muted};
    white-space: nowrap;
  }

  & > button:last-child {
    margin-left: auto;
    flex-shrink: 0;
  }
`;

export const ViewerSlot = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
`;

export const NoDocument = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  background: ${surface.surface};
  border-top-left-radius: ${radius.lg};
  border-top-right-radius: ${radius.lg};
  color: ${text.muted};
  font-size: ${typography.size.md};
  text-align: center;
  padding: ${spacing[6]};
`;

// ---------------------------------------------------------------------------
// Right: the decision on top, the inbox tools under it
// ---------------------------------------------------------------------------

export const RightRail = styled.aside`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: ${INBOX_AI_RAIL_WIDTH}px;
  height: 100%;
  gap: 8px;
  min-height: 0;
`;

export const DecisionPanel = styled.div`
  background: ${surface.surface};
  border-radius: ${radius.lg};
  padding: ${spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${spacing[2.5]};
`;

export const PanelLabel = styled.label`
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.semibold};
  color: ${text.secondary};
`;

export const NoteBox = styled.textarea`
  font-family: inherit;
  font-size: ${typography.size.base};
  border: 1px solid
    ${(p) => (p.$invalid ? status.failed.border : border.default)};
  border-radius: ${radius.md};
  padding: ${spacing[2]} ${spacing[2.5]};
  min-height: 72px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${brand.primary};
  }
`;

export const DateInput = styled.input`
  font-family: inherit;
  font-size: ${typography.size.base};
  border: 1px solid ${border.default};
  border-radius: ${radius.md};
  padding: ${spacing[1.5]} ${spacing[2.5]};
  width: 180px;
`;

export const ButtonRow = styled.div`
  display: flex;
  gap: ${spacing[2]};

  > button {
    flex: 1;
  }
`;

const buttonBase = css`
  font-family: inherit;
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.semibold};
  padding: ${spacing[2.5]} ${spacing[4]};
  border-radius: ${radius.md};
  cursor: pointer;
  border: 1px solid transparent;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const SignUpButton = styled.button`
  ${buttonBase};
  background: ${status.ready.fg};
  color: ${text.onAccent};

  &:hover:not(:disabled) {
    filter: brightness(1.08);
  }
`;

export const TurnDownButton = styled.button`
  ${buttonBase};
  background: ${surface.surface};
  color: ${status.failed.fg};
  border-color: ${status.failed.border};

  &:hover:not(:disabled) {
    background: ${status.failed.bg};
  }
`;

export const SmallPrint = styled.div`
  font-size: ${typography.size.sm};
  color: ${text.muted};
`;

export const ToolsSlot = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: ${surface.surface};
  border-top-left-radius: ${radius.lg};
  border-top-right-radius: ${radius.lg};
  overflow: hidden;
`;

// ---------------------------------------------------------------------------
// Right, under the decision: the emails about this referral
// ---------------------------------------------------------------------------

export const MailHeader = styled.div`
  padding: ${spacing[3]} ${spacing[4]} ${spacing[2]};
  border-bottom: 1px solid ${border.light};
  display: flex;
  align-items: baseline;
  gap: ${spacing[2]};
`;

export const MailTitle = styled.h3`
  margin: 0;
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.bold};
  color: ${text.primary};
`;

export const MailScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${spacing[2]};
`;

export const MailRow = styled.button`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${spacing[0.5]};
  text-align: left;
  font-family: inherit;
  background: ${(p) => (p.$active ? brand.primaryLight : surface.surface)};
  border: 1px solid ${(p) => (p.$active ? brand.primary : border.light)};
  border-radius: ${radius.md};
  padding: ${spacing[2]} ${spacing[3]};
  margin-bottom: ${spacing[1.5]};
  cursor: pointer;
  ${(p) =>
    p.$open &&
    css`
      margin-bottom: 0;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    `}

  &:hover {
    border-color: ${brand.primary};
  }
`;

export const MailWho = styled.span`
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.semibold};
  color: ${text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const MailWhen = styled.span`
  font-size: ${typography.size.xs || typography.size.sm};
  color: ${text.muted};
  flex-shrink: 0;
`;

export const MailSubject = styled.span`
  font-size: ${typography.size.sm};
  color: ${text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const MailPreview = styled.span`
  font-size: ${typography.size.sm};
  color: ${text.secondary};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const ReplyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  margin: 0 0 ${spacing[2]} ${spacing[1]};
  border: 1px solid ${brand.primary};
  background: ${surface.surface};
  color: ${brand.primary};
  font-family: inherit;
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.semibold};
  border-radius: ${radius.md};
  padding: ${spacing[1]} ${spacing[3]};
  cursor: pointer;

  &:hover {
    background: ${brand.primaryLight};
  }
`;

export const ReplyBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1.5]};
  margin: 0 0 ${spacing[2]};
  padding: ${spacing[2.5]} ${spacing[3]};
  border: 1px solid ${brand.primary};
  border-radius: ${radius.md};
  background: ${surface.page};
`;

export const ReplyField = styled.input`
  font-family: inherit;
  font-size: ${typography.size.sm};
  border: 1px solid ${border.default};
  border-radius: ${radius.md};
  padding: ${spacing[1]} ${spacing[2]};

  &:focus {
    outline: none;
    border-color: ${brand.primary};
  }
`;

export const ReplyActions = styled.div`
  display: flex;
  gap: ${spacing[2]};

  > button {
    flex: 1;
    padding: ${spacing[1.5]} ${spacing[3]};
  }
`;

export const MailAttachments = styled.span`
  font-size: ${typography.size.xs || typography.size.sm};
  color: ${text.muted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// An email opened in place under its row.
export const MailOpen = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  margin: 0 0 ${spacing[2]};
  padding: ${spacing[2.5]} ${spacing[3]};
  border: 1px solid ${brand.primary};
  border-top: none;
  border-bottom-left-radius: ${radius.md};
  border-bottom-right-radius: ${radius.md};
  background: ${surface.surface};
`;

export const MailMeta = styled.dl`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${spacing[0.5]} ${spacing[2]};
  margin: 0;
  font-size: ${typography.size.xs || typography.size.sm};

  dt {
    color: ${text.muted};
  }

  dd {
    margin: 0;
    color: ${text.secondary};
    overflow-wrap: anywhere;
  }
`;

export const MailBody = styled.div`
  font-size: ${typography.size.sm};
  line-height: 1.45;
  color: ${(p) => (p.$earlier ? text.secondary : text.primary)};
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  max-height: 360px;
  overflow-y: auto;
  ${(p) =>
    p.$earlier &&
    css`
      padding-left: ${spacing[2]};
      border-left: 2px solid ${border.default};
    `}
`;

export const MailLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[1]} ${spacing[3]};

  > ${LinkButton} {
    margin-left: 0;
  }
`;

export const AttachmentList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing[0.5]};

  a,
  button {
    display: inline-flex;
    align-items: center;
    gap: ${spacing[1]};
    border: none;
    background: none;
    padding: 0;
    font-family: inherit;
    font-size: ${typography.size.sm};
    color: ${brand.primary};
    text-align: left;
    text-decoration: none;
    cursor: pointer;
  }

  a:hover,
  button:hover {
    text-decoration: underline;
  }
`;

// Firm addresses offered while typing in To or Cc.
export const AddressWrap = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

export const Suggestions = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 5;
  margin: 2px 0 0;
  padding: ${spacing[1]} 0;
  list-style: none;
  background: ${surface.surface};
  border: 1px solid ${border.default};
  border-radius: ${radius.md};
  box-shadow: 0 4px 12px rgba(16, 24, 40, 0.12);
  max-height: 240px;
  overflow-y: auto;
`;

export const Suggestion = styled.li`
  display: flex;
  flex-direction: column;
  padding: ${spacing[1]} ${spacing[2]};
  cursor: pointer;
  background: ${(p) => (p.$active ? brand.primaryLight : "transparent")};

  strong {
    font-size: ${typography.size.sm};
    font-weight: ${typography.weight.semibold};
    color: ${text.primary};
  }

  span {
    font-size: ${typography.size.xs || typography.size.sm};
    color: ${text.muted};
  }
`;
